/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { colors } from "@farmlands/mui-theme";
import { Box, Divider as MuiDivider, Stack, styled, useMediaQuery, useTheme } from "@mui/material";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { BackButton } from "../../components/BackButton";
import ContactCard, { ContactContainer, ContactItem } from "../../components/ContactCard";
import HelperMessage from "../../components/HelperMessage";
import { PageContainer } from "../../components/layout/PageContainer";
import { Loader } from "../../components/Loader";
import MaterialSymbol from "../../components/MaterialSymbol";
import { OrderSummary } from "../../components/OrderSummary";
import ShareButton from "../../components/ShareButton";
import StatusChip from "../../components/StatusChip";
import { StatusHeader } from "../../components/StatusHeader";
import { ItemContainer, ItemLabel } from "../../components/styled";
import Typography from "../../components/Typography";
import YesNoAction from "../../components/YesNoAction";
import apiClient from "../../services/api-client";
import { useAppDispatch } from "../../store/rootStore";
import { TestIds } from "../../testIds";
import { calculateOrderDiscount } from "../../util/calculateEditedPriceDiscount";
import { CONTENT_LAYOUT_WIDTH } from "../../util/consts";
import { formatPrice } from "../../util/helpers";
import AuthSlice, { selectCurrentFarmerAccount } from "../azure-b2c/storage/auth.slice";
import { selectPickupStores, selectStoreById } from "../stores/storage/stores.slice";

import OrderedProductCard from "./components/OrderedProductCard";
import { transformOrderResponse } from "./storage/orders.slice";
import { DeliveryMode, GetOrderStatusType, OrderStatus, SalesOrder } from "./storage/types";
import { getOrderDeliveryDisplay } from "./utils/getDeliveryDisplay";
import { mapStatus } from "./utils/getOrderStatus";
import { getOrderTypeFromDeliveryMode } from "./utils/getOrderType";
import { hasMultiStores } from "./utils/hasMultiStores";

const MainContainer = styled("div")({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "24px",
});

const BodyContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  background: colors.shade0,
  padding: "24px",
  gap: "24px",
  border: `1px solid ${colors.primary100}`,
  [theme.breakpoints.down("sm")]: {
    padding: "16px",
    gap: "16px",
  },
}));

const Container = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
});

const PurchaseContainer = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: "40px",
});

const StoreWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  gap: "24px",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    gap: "16px",
  },
}));

const NotesContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
});

const HelperContentWrapper = styled("div")(({ theme }) => ({
  background: theme.colors.primary100,
  color: theme.colors.primary500,
  display: "flex",
  lineHeight: "21px",
  alignItems: "center",
  paddingLeft: "16px",
  maxWidth: "688px",
}));

const BasicInfo = styled("div")(({ theme }) => ({
  display: "flex",
  columnGap: "48px",
  rowGap: "16px",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
  },
}));

const InnerContainer = styled("div")({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  gap: "8px",
});

const Divider = styled(MuiDivider)(({ theme }) => ({
  borderColor: theme.colors.primary200,
  margin: "8px 0",
}));

const OrderDetails = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMedium = useMediaQuery(theme.breakpoints.down("md"));
  const account = useSelector(selectCurrentFarmerAccount);
  const { orderId } = useParams();

  const [order, setOrder] = React.useState<SalesOrder>();
  const stores = useSelector(selectPickupStores);
  const store = useSelector(
    selectStoreById(
      order?.salesLines && order.salesLines.length > 0
        ? order?.salesLines[0].inventoryLocationId
        : undefined
    )
  );

  const [status, setStatus] = React.useState<GetOrderStatusType>(null);
  const [isAccountMismatch, setIsAccountMismatch] = useState(false);

  useEffect(() => {
    if (account && orderId) {
      if (!order || order?.accountNumber === account.accountNumber) {
        setStatus("pending");
        apiClient
          .get<SalesOrder, string>(`/orders/${orderId}`)
          .then((request) => {
            setStatus("fulfilled");
            setOrder(transformOrderResponse(request.data));
          })
          .catch(() => setStatus("rejected"));
        setIsAccountMismatch(false);
      } else if (orderId) {
        setIsAccountMismatch(true);
      }
    }
  }, [account, orderId, order?.accountNumber]);

  const loading = React.useMemo(() => {
    return status !== "fulfilled";
  }, [status]);

  const mappedStatus = React.useMemo(() => {
    if (order) {
      return mapStatus(order.detailedStatus, order.deliveryMode, order.createdAt);
    }
  }, [order]);

  const isMultipleDeliveries = hasMultiStores(order?.salesLines);
  const isOrderLineStatusVisible = React.useMemo(() => {
    if (
      !order?.detailedStatus ||
      order?.detailedStatus === OrderStatus.Delivered ||
      order?.detailedStatus === OrderStatus.Invoiced
    ) {
      return false;
    }
    return true;
  }, [order?.detailedStatus]);

  const fromText = React.useMemo(() => {
    if (order?.deliveryMode === DeliveryMode.Pickup) {
      return "Pick up from: ";
    }
    if (order?.deliveryMode === DeliveryMode.Delivery) {
      return "Sending from: ";
    }
    return "Purchased from: ";
  }, [order?.deliveryMode]);

  const renderTitle = React.useMemo(() => {
    if (order) {
      return (
        <Box mb={3}>
          <StatusHeader
            title={`Order #${order.salesId}`}
            statusChip={
              mappedStatus && (
                <StatusChip
                  label={mappedStatus.display}
                  variant="filled"
                  bgColor={mappedStatus.color.bg}
                  textColor={mappedStatus.color.text}
                  data-testid={TestIds.components.statusHeader.statusBadge}
                />
              )
            }
            text={
              <>
                <Typography
                  variant="body2"
                  color="grey800"
                  data-testid={TestIds.feat.orders.details.orderPlaced}
                >
                  Order placed: {dayjs(order.createdAt).format("D MMM YYYY")}
                </Typography>
                {order.customerReference && (
                  <Typography
                    variant="body2"
                    color="grey800"
                    data-testid={TestIds.feat.orders.details.customerReference}
                  >
                    Your reference: {order.customerReference}
                  </Typography>
                )}
              </>
            }
          />
        </Box>
      );
    }
  }, [order, mappedStatus, store]);

  const renderHelperText = React.useMemo(
    () => (
      <HelperContentWrapper>
        <MaterialSymbol variant="fill">error</MaterialSymbol>
        <HelperMessage
          title="Your order is coming from multiple locations"
          body="Due to item availability, we had to split your order into multiple deliveries from
          different locations. You will not be charged any additional delivery fees."
          show={true}
          sx={{ border: "none", pt: 1, px: 2, m: 0 }}
        />
      </HelperContentWrapper>
    ),
    []
  );

  const renderBasicInfo = React.useMemo(
    () =>
      order && (
        <>
          <Container>
            <BasicInfo>
              <ItemContainer wrap={false} data-testid={TestIds.feat.orders.details.totalCost}>
                <ItemLabel variant="body2">Total cost:</ItemLabel>
                <ItemLabel variant="h2" color={"grey800"}>
                  {order && formatPrice(order.totalPrice)}
                </ItemLabel>
              </ItemContainer>
              {order.discountIncGst > 0 && (
                <ItemContainer wrap={false} data-testid={TestIds.feat.orders.details.totalSavings}>
                  <ItemLabel variant="body2">Savings:</ItemLabel>
                  <ItemLabel variant="h2" color={"secondary700"}>
                    {order && calculateOrderDiscount(order)}
                  </ItemLabel>
                </ItemContainer>
              )}

              <ItemContainer wrap={false} data-testid={TestIds.feat.orders.details.orderType}>
                <ItemLabel variant="body2">Order type:</ItemLabel>
                <ItemLabel variant="h2">
                  {getOrderTypeFromDeliveryMode(order?.deliveryMode)}
                </ItemLabel>
              </ItemContainer>
            </BasicInfo>
          </Container>
          <Divider />
        </>
      ),
    [order]
  );

  const getWarePickup = () => {
    return (
      <>
        {order?.shippingAddress && (
          <InnerContainer data-testid={TestIds}>
            <Typography variant="body1" color="grey700">
              Pick up from:
            </Typography>
            <Typography
              variant="h2"
              color="grey800"
              data-testid={TestIds.feat.orders.deliveryDetails.deliveryName}
            >
              {order?.shippingAddress?.name}
            </Typography>
            <Typography
              variant="body2"
              color="grey800"
              data-testid={TestIds.feat.orders.deliveryDetails.address}
            >
              {order?.shippingAddress?.fullAddress}
            </Typography>
          </InnerContainer>
        )}
      </>
    );
  };

  const renderDeliveryDetails = React.useMemo(() => {
    return (
      <>
        {order?.shippingAddress && (
          <InnerContainer data-testid={TestIds}>
            <Typography variant="body1" color="grey700">
              Delivery to:
            </Typography>
            <Typography
              variant="h2"
              color="grey800"
              data-testid={TestIds.feat.orders.deliveryDetails.deliveryName}
            >
              {order?.shippingAddress?.name}
            </Typography>
            <Typography
              variant="body2"
              color="grey800"
              data-testid={TestIds.feat.orders.deliveryDetails.address}
            >
              {order?.shippingAddress?.fullAddress}
            </Typography>
          </InnerContainer>
        )}
      </>
    );
  }, [store, order?.shippingAddress]);

  const renderStore = React.useMemo(
    () => (
      <InnerContainer>
        <Typography
          variant="body1"
          color="grey700"
          data-testid={TestIds.feat.orders.pickUpDetails.orderOriginLabel}
        >
          {fromText}
        </Typography>
        <Typography
          variant="h2"
          color="grey800"
          data-testid={TestIds.feat.orders.pickUpDetails.storeName}
        >
          Farmlands {store?.orgUnitName}
        </Typography>
        <Typography
          variant="body2"
          color="grey800"
          data-testid={TestIds.feat.orders.pickUpDetails.address}
        >
          {store?.address}
        </Typography>
        <ContactContainer>
          {store?.phone && (
            <ContactItem
              href={`tel:${store?.phone}`}
              color={colors.primary500}
              data-testid={TestIds.feat.orders.pickUpDetails.phoneNumber}
            >
              <div className="material-symbols-outlined" style={{ fontWeight: 300 }}>
                phone
              </div>
              <Typography variant="body2" color="primary500">
                {store?.phone}
              </Typography>
            </ContactItem>
          )}
          {store?.email && (
            <ContactItem
              href={`mailto:${store?.email}`}
              color={colors.primary500}
              data-testid={TestIds.feat.orders.pickUpDetails.emailAddress}
            >
              <div className="material-symbols-outlined" style={{ fontWeight: 300 }}>
                email
              </div>
              <Typography variant="body2" color="primary500" sx={{ textDecoration: "underline" }}>
                {store?.email}
              </Typography>
            </ContactItem>
          )}
        </ContactContainer>
      </InnerContainer>
    ),
    [fromText, store]
  );

  const renderStoreDetails = React.useMemo(() => {
    if (
      !store &&
      (order?.deliveryMode === DeliveryMode.Pickup ||
        order?.deliveryMode === DeliveryMode.WarePickup) &&
      order?.shippingAddress
    ) {
      return (
        <>
          <StoreWrapper>{getWarePickup()}</StoreWrapper>
          <Divider sx={{ border: 0 }} />
        </>
      );
    }
    if (store || order?.deliveryMode === DeliveryMode.Delivery) {
      return (
        <>
          <StoreWrapper>
            {store && !isMultipleDeliveries && renderStore}
            {order?.deliveryMode === DeliveryMode.Delivery && renderDeliveryDetails}
          </StoreWrapper>
          <Divider sx={{ border: 0 }} />
        </>
      );
    }
  }, [store, order, renderDeliveryDetails]);

  const renderCardVendor = React.useMemo(() => {
    if (order?.deliveryMode === DeliveryMode.Card && order?.cardVendor) {
      return (
        <Box>
          <Typography variant="body1" color={"grey700"}>
            Purchase made at:
          </Typography>
          <Typography
            variant="h2"
            color={"grey800"}
            pt={1}
            pb={1.5}
            data-testid={TestIds.feat.orders.details.cardVendor}
          >
            {order.cardVendor}
          </Typography>
        </Box>
      );
    }
  }, [order]);

  const renderOrderHistory = React.useMemo(() => {
    return (
      <Container>
        <Typography variant="h4" color={"primary500"}>
          Items ordered
        </Typography>
        {order?.salesLines.map((x) => (
          <OrderedProductCard
            createdAt={order.createdAt}
            deliveryMode={order.deliveryMode}
            item={x}
            key={x.inventoryTransactionId}
            isStatusVisible={
              isOrderLineStatusVisible ||
              x.detailedLineStatus === "CustomerCanceled" ||
              x.detailedLineStatus === "SystemCanceled"
            }
          />
        ))}
      </Container>
    );
  }, [order?.salesLines, order?.deliveryMode, mappedStatus]);

  const renderDiscountedUnitPrice = React.useMemo(() => {
    const hasPriceOverriddenSaleLine = order?.salesLines.some(
      (salesLine) => salesLine.isPriceOverridden
    );

    if (!hasPriceOverriddenSaleLine) {
      return <Divider />;
    }

    return (
      <>
        <Stack
          direction="row"
          alignItems="center"
          px={2}
          py={1}
          spacing={1}
          bgcolor={theme.colors.secondary100}
        >
          <MaterialSymbol sx={{ fontSize: 24, color: theme.colors.secondary800 }}>
            Info
          </MaterialSymbol>
          <Box data-testid={TestIds.feat.orders.details.priceEditedBanner}>
            <Typography variant="body2" color="secondary800" fontWeight="bold">
              Discounted unit prices
            </Typography>
            <Typography variant="caption" color="secondary800" lineHeight="21px">
              Prices may vary from original price, discounts were applied by Farmlands
              representative
            </Typography>
          </Box>
        </Stack>
        <Divider />
      </>
    );
  }, [order?.salesLines]);

  const noteField = (title: string, notes: string) => {
    return (
      <Box mb="8px" sx={{ gap: 0 }}>
        <Typography
          variant="body2"
          component={"p"}
          key={title}
          sx={{
            fontWeight: "600",
            letterSpacing: "0.32px",
            margin: 0,
          }}
        >
          {title}
        </Typography>
        <NotesContainer data-testid={TestIds.feat.orders.details.notes}>
          {notes.split("\n").map((string) => (
            <Typography
              variant="body2"
              component={"p"}
              key={string}
              sx={{ overflowWrap: "break-word" }}
            >
              {string}
            </Typography>
          ))}
        </NotesContainer>
      </Box>
    );
  };

  const renderNotes = React.useMemo(() => {
    const title = order?.deliveryMode === DeliveryMode.Delivery ? "Notes & instructions" : "Notes";
    const notes = order?.notes;
    const deliveryInstructions = order?.deliveryInstructions;
    const dairySupplyNumber = order?.dairySupplyNumber;

    if (notes || deliveryInstructions || dairySupplyNumber) {
      return (
        <>
          <Container>
            <Typography variant="h4" color={"primary500"}>
              {title}
            </Typography>
            <NotesContainer>
              {notes ? noteField("Notes", notes) : null}
              {deliveryInstructions
                ? noteField("Delivery Instructions", deliveryInstructions)
                : null}
              {dairySupplyNumber ? noteField("Dairy Supply Number", dairySupplyNumber) : null}
            </NotesContainer>
          </Container>
          <Divider />
        </>
      );
    }

    return null;
  }, [order?.notes]);

  const renderInternalNotes = React.useMemo(() => {
    if (!account?.hasTfoAccess) {
      return;
    }

    const internalNotes = order?.internalNotes ? order?.internalNotes : "No internal notes added.";
    return (
      <>
        <Container>
          <Typography variant="h4" color={"primary500"}>
            Internal notes
          </Typography>
          <NotesContainer data-testid={TestIds.feat.orders.details.internalNotes}>
            {internalNotes.split("\n").map((string) => (
              <Typography
                variant="body2"
                component={"p"}
                key={string}
                sx={{ overflowWrap: "break-word" }}
              >
                {string}
              </Typography>
            ))}
          </NotesContainer>
        </Container>
        <Divider />
      </>
    );
  }, [order?.internalNotes]);

  const createClipboardText = () => {
    if (order) {
      const text =
        `
Order: ${order.salesId}
Account: ${order.accountNumber}
Account Name: ${account?.accountName}

Order placed: Farmlands ${store?.orgUnitName ?? ""}
${store?.address ?? ""}\n
Customer Ref: ${order.customerReference ?? "N/A"}
Customer Notes: ${order.notes ?? "N/A"}\n
Items Ordered: \n\n` +
        order.salesLines
          .map(
            (e) =>
              `Product: ${e.description}
SKU: ${e.productId}
Status: ${e.detailedLineStatus} 
Quantity: x${e.quantity}\n`
          )
          .join("\n");

      return text;
    }

    return "";
  };

  const renderPurchaseSummary = React.useMemo(() => {
    const deliveryDisplay = getOrderDeliveryDisplay(order);
    return (
      <PurchaseContainer>
        <Container sx={{ width: isMedium ? "100%" : "376px" }}>
          <OrderSummary
            title={"Purchase summary"}
            accountName={account?.accountName}
            accountNumber={account?.accountNumber}
            total={formatPrice(order?.totalPrice ?? 0)}
            gst={formatPrice(order?.gstAmount ?? 0)}
            delivery={deliveryDisplay}
            discount={calculateOrderDiscount(order)}
          />
        </Container>

        <ShareButton
          label="Share this order"
          content={{
            title: "Farmlands Order: ",
            text: createClipboardText(),
          }}
        />
      </PurchaseContainer>
    );
  }, [order, isMedium]);

  // Contact card hierarchy in Order Details page :
  // Show STORE INFO where a user placed its order if the order comes from 1 store
  // Show GENERIC(not TFO) if there the order comes from multi stores or the order type is Card
  const renderContactCard = React.useMemo(() => {
    const relationshipOwner = {
      email: "ask@farmlands.co.nz",
      phone: "0800 200 600",
    };

    if (
      store &&
      !isMultipleDeliveries &&
      stores.find((x) => x.inventorySiteId === store.inventorySiteId)
    ) {
      return (
        <ContactCard
          title="If you have any questions about your order please contact the store directly."
          body={
            <Typography
              variant="body1"
              color="shade0"
              fontWeight={700}
              lineHeight={"27px"}
              data-testid={TestIds.components.contactCard.storeName}
            >
              <span>{store?.orgUnitName}</span>
              <br />
              <Typography
                variant="body1"
                color="shade0"
                data-testid={TestIds.components.contactCard.storeAddress}
              >
                {store?.address}
              </Typography>
            </Typography>
          }
          email={store?.email}
          phone={store?.phone}
          titleGap={24}
          storeName={store?.orgUnitName}
          data-testid={TestIds.components.contactCard.card}
        />
      );
    }
    return (
      <ContactCard
        title="If you have any questions about your order please contact a Farmlands representative."
        {...relationshipOwner}
        data-testid={TestIds.components.contactCard.card}
      />
    );
  }, [store]);

  const renderNoAccountMismatch = useMemo(
    () => (
      <>
        <BackButton text="All your orders" onClick={() => navigate("/orders")} />
        {account && (
          <Box mb={2}>
            <YesNoAction
              display="Order not available"
              body={
                <span>
                  <span>Account: {account?.accountName}</span>
                  <br />
                  <span>Account number: {account?.accountNumber}</span>
                  <br />
                  <br />
                  Looks like this order is associated with a different account. If you have other
                  accounts with Farmlands you can switch using the account selector.
                </span>
              }
              optionA={{
                label: "Switch back to " + account.accountName,
                onClick: () => {
                  dispatch(AuthSlice.actions.setSelectedFarmerAccount(account.accountNumber));
                },
              }}
              optionB={{
                label: "Back to Orders",
                onClick: () => {
                  navigate("/orders");
                },
              }}
            />
          </Box>
        )}
      </>
    ),
    [isAccountMismatch]
  );

  return (
    <PageContainer
      data-testid={TestIds.pages.orderDetails}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        maxWidth: `${CONTENT_LAYOUT_WIDTH}px`,
      }}
    >
      {loading && <Loader />}
      {!loading && !isAccountMismatch && (
        <>
          <BackButton text="All your orders" onClick={() => navigate("/orders")} />
          {renderTitle}
          <MainContainer>
            {isMultipleDeliveries && renderHelperText}
            <BodyContainer>
              {renderBasicInfo}
              {renderStoreDetails}
              {renderCardVendor}
              {renderOrderHistory}
              {renderDiscountedUnitPrice}
              {renderNotes}
              {renderInternalNotes}
              {renderPurchaseSummary}
            </BodyContainer>
            {renderContactCard}
          </MainContainer>
        </>
      )}
      {!loading && isAccountMismatch && renderNoAccountMismatch}
    </PageContainer>
  );
};

export default OrderDetails;
